import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useScanGun from '../../hooks/useScanGun'
import { shelfContents } from '../../utils/warehouseApi'
import { signal } from '../../utils/feedback'
import { resolveScan } from './resolveScan'
import ScanPrompt from './ScanPrompt'
import ItemRows from './ItemRows'
import PdaPhotos from './PdaPhotos'
import NeedsLabelButton from './NeedsLabelButton'

/**
 * Scan a location to see what is in it, or scan an item to find where it is.
 * One screen, either direction — the operator should not have to know which
 * kind of label they are pointing at.
 *
 * Rows are tappable and hand off into Consolidate with the item already in
 * hand, because "what is in here?" is almost always followed by "and that one
 * needs to move".
 *
 * Walking a row of totes means scanning the next one before the last has
 * finished loading, so nothing here waits its turn: a newer scan supersedes an
 * older one, and the generation stamp stops a slow earlier response landing on
 * top of a later one.
 *
 * The tote being viewed lives in the URL as `?at=`. Tapping a row to move an
 * item pushes a history entry, so pressing back returns here — and because the
 * tote is in the address rather than in component state, it comes back to the
 * same open tote instead of an empty screen.
 */
export default function PdaLookup() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [location, setLocation] = useState(null)
    const [contents, setContents] = useState([])
    const [item, setItem] = useState(null)
    const [banner, setBanner] = useState(null)
    const [busy, setBusy] = useState(false)
    // Bumped by every scan and every lookup. A response whose stamp is no
    // longer the current one belongs to a location the operator has moved on
    // from, and must not paint over what they are looking at now.
    const generation = useRef(0)

    const openLocation = useCallback(async (shelfId) => {
        const mine = ++generation.current
        setBusy(true)
        setItem(null)
        try {
            const response = await shelfContents(shelfId)
            if (mine !== generation.current) return
            setContents(Array.isArray(response) ? response : response?.items ?? [])
            setLocation(shelfId)
            // Replace rather than push: scanning a row of totes should not fill
            // the history with every one of them.
            setSearchParams({ at: shelfId }, { replace: true })
            signal('ok')
        } catch (error) {
            if (mine !== generation.current) return
            setBanner({ kind: 'error', text: error.message || 'Could not read that location.' })
            signal('problem')
        } finally {
            if (mine === generation.current) setBusy(false)
        }
    }, [])

    // Restore the tote named in the URL, so returning from Consolidate lands
    // back in the list the operator was working through.
    const openFromUrl = searchParams.get('at')
    useEffect(() => {
        if (openFromUrl && openFromUrl !== location) openLocation(openFromUrl)
        // Only re-runs when the address changes; `location` is deliberately not
        // a dependency or reopening would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openFromUrl])

    const onScan = useCallback(async (raw) => {
        const mine = ++generation.current
        setBanner(null)
        const result = await resolveScan(raw)
        if (mine !== generation.current) return

        if (result.kind === 'error') {
            setBanner({ kind: 'error', text: result.message })
            signal('problem')
            return
        }
        if (result.kind === 'location') {
            openLocation(result.location.shelf_id)
            return
        }

        setLocation(null)
        setContents([])
        setSearchParams({}, { replace: true })
        setItem(result.item)
        if (result.item.current_shelf_id) {
            signal('ok')
        } else {
            setBanner({ kind: 'warn', text: 'This item has no location yet. Put it away first.' })
            signal('problem')
        }
    }, [openLocation])

    // Deliberately always listening. Gating this on `busy` tore the listener
    // down mid-request and cleared its buffer, so a scan arriving before the
    // previous lookup returned was swallowed whole.
    useScanGun(onScan)

    return (
        <>
            <ScanPrompt
                prompt="Scan anything"
                hint="A tote or shelf to see inside it, or an item to find it."
                placeholder="Locator code"
                onSubmit={onScan}
                disabled={busy}
            />

            {banner && <div className={`pda-banner is-${banner.kind}`}>{banner.text}</div>}

            {item && (
                <div className="pda-held">
                    <div className="pda-held-title">{item.title}</div>
                    <div className="pda-code">{item.item_id}</div>
                    {item.current_shelf_id && (
                        <>
                            <div className="pda-location">{item.current_shelf_id}</div>
                            <button className="pda-btn" onClick={() => openLocation(item.current_shelf_id)}>
                                See what else is in there
                            </button>
                        </>
                    )}
                    <PdaPhotos item={item} />
                    <NeedsLabelButton item={item} disabled={busy} />
                </div>
            )}

            {location && (
                <>
                    <div className="pda-location">{location}</div>
                    <div className="pda-sub">
                        {contents.length} {contents.length === 1 ? 'item' : 'items'}
                    </div>
                    <ItemRows
                        items={contents}
                        emptyText="Prism says this location is empty."
                        showLocation
                        onPick={(picked) => navigate('/pda/consolidate', { state: { item: picked } })}
                    />
                </>
            )}
        </>
    )
}
